import React, { useCallback, useEffect, useState } from 'react';
import { Gauge, Sparkles, X } from 'lucide-react';
import { apiService } from '../../services/api';
import { ago } from './review';

// Accueil : la revue d'avancement pour qui tient la roadmap d'au moins un projet. D'habitude une ligne avec son bouton
// « Lancer une revue » ; mise en avant par Nexus avant une tactique, ou quand des projets n'ont pas été notés depuis
// une semaine. « Plus tard » replie la proposition de Nexus pour la journée (le bouton reste).

const REFRESH_MS = 5 * 60000;
const hiddenKey = (day) => `deepflow.home.progressReview.later.${day}`;

const readLater = (day) => {
  try {
    return localStorage.getItem(hiddenKey(day)) === '1';
  } catch (err) {
    return false;
  }
};

const ProgressReviewCard = ({ today, navigate }) => {
  const [state, setState] = useState(null);
  const [later, setLater] = useState(() => readLater(today));

  const load = useCallback(() => apiService.get('/progress-review').then(setState).catch(() => setState(null)), []);
  useEffect(() => {
    load();
    const timer = setInterval(() => { if (document.visibilityState === 'visible') load(); }, REFRESH_MS);
    return () => clearInterval(timer);
  }, [load]);
  useEffect(() => { setLater(readLater(today)); }, [today]);

  if (!state?.projects?.length) return null;
  const open = () => navigate('/revue-avancement');
  const postpone = () => {
    setLater(true);
    try {
      localStorage.setItem(hiddenKey(today), '1');
    } catch (err) {
      // stockage indisponible : replié pour cette visite seulement
    }
  };

  if (state.suggest && !later) {
    return (
      <section className="flex flex-wrap items-center gap-3 rounded-2xl border border-indigo-200 bg-white px-4 py-3 shadow-sm sm:flex-nowrap sm:px-5">
        <span className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-indigo-600 text-white shadow-sm shadow-indigo-200">
          <Sparkles size={15} />
        </span>
        <div className="min-w-0 flex-1">
          <p className="text-[11px] font-semibold uppercase tracking-wide text-indigo-500">Revue d’avancement</p>
          <p className="text-sm leading-relaxed text-slate-700">{state.message}</p>
        </div>
        <div className="flex shrink-0 items-center gap-1">
          <button type="button" onClick={open}
            className="flex items-center gap-1.5 rounded-lg bg-indigo-600 px-3 py-1.5 text-sm font-semibold text-white shadow-sm hover:bg-indigo-700">
            <Gauge size={14} /> Faire la revue
          </button>
          <button type="button" onClick={postpone} title="Plus tard (repliée jusqu’à demain)"
            className="rounded-md p-1.5 text-slate-300 hover:bg-slate-100 hover:text-slate-500">
            <X size={14} />
          </button>
        </div>
      </section>
    );
  }

  // Hors proposition de Nexus, le leader de projet garde toujours de quoi lancer la revue lui-même (Rémy, 23/10).
  const seen = ago(state.last_seen_at);
  const tracked = state.projects.filter((p) => p.iterations?.length).length; // sans itération en cours : rien à noter
  return (
    <section className="flex flex-wrap items-center gap-x-3 gap-y-2 rounded-2xl border border-slate-200/80 bg-white px-4 py-2.5 shadow-sm sm:flex-nowrap sm:px-5">
      <p className="min-w-0 basis-full text-sm text-slate-500 sm:flex-1 sm:basis-auto sm:truncate">
        <Gauge size={16} className="mr-2 inline-block align-[-3px] text-indigo-500" />
        <span className="font-medium text-slate-800">Revue d’avancement</span>
        {' · '}{state.due
          ? `${state.due} projet${state.due > 1 ? 's' : ''} à revoir sur ${tracked}`
          : `${tracked} projet${tracked > 1 ? 's' : ''} à jour`}
        {seen ? ` · dernier point ${seen}` : ''}
      </p>
      <button type="button" onClick={open}
        className="flex shrink-0 items-center gap-1.5 rounded-lg border border-slate-200 px-3 py-1.5 text-sm font-semibold text-slate-700 hover:border-indigo-300 hover:text-indigo-700">
        <Gauge size={14} /> Lancer une revue
      </button>
    </section>
  );
};

export default ProgressReviewCard;
